import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { post, useData, useSession } from '@yingbaba/api';
import {
  Select,
  DatePicker,
  ActionDialog,
  Button,
  ErrorNotice,
  PageTitle,
  Pagination,
  QueryState,
  Stat,
  Status,
} from '@yingbaba/ui';
import { Activity, AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import { datetime, money } from '@yingbaba/utils';
import type { FinanceOverview } from '@yingbaba/types';

type FinancialException = {
  id: string;
  kind: string;
  local_entity_id: string;
  provider: string;
  severity: string;
  status: string;
  code: string;
  message: string;
  external_reference: string;
  amount_minor: number;
  currency: string;
  created_at: string;
  updated_at: string;
  age_seconds: number;
};
type FinancialExceptionPage = {
  items: FinancialException[];
  total: number;
  page: number;
  page_size: number;
  summary: {
    open: number;
    critical: number;
    high: number;
    reconciliation: number;
    pending_or_unknown: number;
    failed: number;
  };
};
type ReconciliationBatch = {
  id: string;
  provider: string;
  business_date: string;
  mode: string;
  status: string;
  checked_count: number;
  matched_count: number;
  exception_count: number;
  error_count: number;
  started_at: string;
  completed_at: string;
  last_error: string;
};
type ReconciliationBatchPage = {
  items: ReconciliationBatch[];
  total: number;
  page: number;
  page_size: number;
};

const kinds = [
  ['', '全部类型'],
  ['reconciliation', '对账差异'],
  ['payment_intent', '支付单'],
  ['split', '分账'],
  ['refund', '退款'],
  ['split_reversal', '分账回退'],
] as const;
const providers = [
  ['', '全部渠道'],
  ['wechat', '微信支付'],
  ['alipay', '支付宝'],
  ['unionpay', '银联 / 银行卡'],
] as const;

function providerLabel(value: string) {
  return providers.find(([v]) => v === value)?.[1] || value || '—';
}
function kindLabel(value: string) {
  return kinds.find(([v]) => v === value)?.[1] || value;
}
function severityLabel(value: string) {
  return { critical: '严重', high: '高', medium: '中' }[value] || value;
}
function ageLabel(seconds: number) {
  if (seconds < 60) return '刚刚';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时`;
  return `${Math.floor(seconds / 86400)} 天`;
}
const targetPaths = {
  payment_intent: '/payments',
  split: '/split-transactions',
  refund: '/refunds',
  split_reversal: '/split-reversals',
  reconciliation: '/reconciliation',
} as const;
function targetPath(kind: string) {
  return targetPaths[kind as keyof typeof targetPaths];
}
function financialStatusLabel(kind: string, status: string) {
  const maps: Record<string, Record<string, string>> = {
    payment_intent: {
      creating: '创建中',
      pending: '待支付确认',
      succeeded: '已支付',
      failed: '支付失败',
      cancelled: '已取消',
      expired: '已过期',
    },
    split: {
      ready: '待提交',
      pending: '待查单',
      succeeded: '已分账',
      failed: '分账失败',
      cancelled: '已取消',
    },
    refund: {
      manual_review: '待人工退款',
      ready: '待退款',
      pending: '退款处理中',
      succeeded: '已退款',
      failed: '退款失败',
      cancelled: '已取消',
    },
    split_reversal: {
      ready: '待回退',
      pending: '回退处理中',
      succeeded: '已回退',
      failed: '回退失败',
      cancelled: '已取消',
    },
    reconciliation: {
      matched: '已匹配',
      resolved: '已处理',
      amount_mismatch: '金额不一致',
      currency_mismatch: '币种不一致',
      reference_mismatch: '单号不一致',
      status_mismatch: '状态不一致',
      missing_local: '本地缺失',
      provider_query_failed: '渠道查询失败',
    },
  };
  return maps[kind]?.[status] || status;
}

function batchStatusLabel(status: string) {
  return (
    {
      pending: '等待执行',
      running: '运行中',
      succeeded: '已完成',
      partial: '部分完成',
      failed: '失败',
    }[status] || status
  );
}

export function FinanceWorkbench() {
  const session = useSession();
  const role = session.data?.account.platform_role || '';
  const canWrite = role === 'super_admin' || role === 'finance';
  const [kind, setKind] = useState('');
  const [provider, setProvider] = useState('');
  const [page, setPage] = useState(1);
  const [runProvider, setRunProvider] = useState('');
  const [businessDate, setBusinessDate] = useState(() => {
    const now = new Date();
    now.setDate(now.getDate() - 1);
    return now.toISOString().slice(0, 10);
  });
  const [running, setRunning] = useState(false);
  const [refreshing, setRefreshing] = useState('');
  const [error, setError] = useState<unknown>(null);
  const qc = useQueryClient();
  const exceptionPath = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), page_size: '20' });
    if (kind) params.set('kind', kind);
    if (provider) params.set('provider', provider);
    return `/admin/finance/exceptions?${params}`;
  }, [page, kind, provider]);
  const exceptions = useData<FinancialExceptionPage>(exceptionPath, true, 30_000);
  const overview = useData<FinanceOverview>('/admin/finance/overview', true, 60_000);
  const batches = useData<ReconciliationBatchPage>(
    '/admin/reconciliation/batches?page=1&page_size=10',
    true,
    60_000,
  );

  async function runReconciliation() {
    if (!canWrite || running) return;
    setRunning(true);
    setError(null);
    try {
      await post('/admin/reconciliation/run', {
        provider: runProvider,
        business_date: businessDate,
      });
      await qc.invalidateQueries();
    } catch (e) {
      setError(e);
    } finally {
      setRunning(false);
    }
  }

  async function refreshException(item: FinancialException) {
    if (!canWrite || item.kind === 'reconciliation' || refreshing) return;
    setRefreshing(item.id);
    setError(null);
    try {
      await post(
        `/admin/finance/exceptions/${encodeURIComponent(item.kind)}/${encodeURIComponent(item.local_entity_id)}/refresh`,
        {},
      );
      await qc.invalidateQueries();
    } catch (e) {
      setError(e);
    } finally {
      setRefreshing('');
    }
  }

  return (
    <>
      <PageTitle
        title="财务异常工作台"
        description="自动核验支付、分账、退款和分账回退状态，把渠道差异与长时间未收敛的资金状态集中到一个处理入口。"
        action={
          <Link className="btn btn-secondary" to="/reconciliation">
            查看全部对账记录
          </Link>
        }
      />
      <ErrorNotice error={error} />
      <ErrorNotice error={overview.error} />
      <div className="stat-grid">
        <Stat
          label="严重异常"
          value={exceptions.data?.summary.critical ?? '…'}
          foot="金额 / 币种 / 单号等关键差异"
          icon={<AlertTriangle size={17} />}
        />
        <Stat
          label="高优先级异常"
          value={exceptions.data?.summary.high ?? '…'}
          foot="优先查单与核实"
          icon={<ShieldAlert size={17} />}
        />
        <Stat
          label="今日待审核付款"
          value={overview.data?.today_pending_payments ?? '…'}
          foot="按北京时间统计今日提交的付款"
          icon={<Activity size={17} />}
        />
        <Stat
          label="失败对账批次"
          value={overview.data?.failed_batches ?? '…'}
          foot="失败或部分失败，待复核"
          icon={<RefreshCw size={17} />}
        />
        <Stat
          label="待结算金额"
          value={overview.data ? money(overview.data.pending_settlement_minor) : '…'}
          foot="已具备结算条件、尚未记录结算"
          icon={<Activity size={17} />}
        />
      </div>

      <section className="panel stack" style={{ marginTop: 18 }}>
        <div className="panel-title">
          <h3>优先处理</h3>
          <span className="small muted">
            严重与高优先级异常 · 共{' '}
            {exceptions.data
              ? exceptions.data.summary.critical + exceptions.data.summary.high
              : '…'}{' '}
            项待处理
          </span>
        </div>
        {exceptions.data?.items
          .filter((item) => item.severity === 'critical' || item.severity === 'high')
          .slice(0, 5)
          .map((item) => (
            <div className="list-item" key={item.id}>
              <div className="grow">
                <strong>
                  {severityLabel(item.severity)} · {kindLabel(item.kind)} ·{' '}
                  {providerLabel(item.provider)}
                </strong>
                <p>{item.message}</p>
              </div>
              {canWrite && item.kind !== 'reconciliation' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={refreshing === item.id}
                  onClick={() => refreshException(item)}
                >
                  立即查单
                </Button>
              ) : (
                <Link className="link small" to={targetPath(item.kind) || '/reconciliation'}>
                  查看详情
                </Link>
              )}
            </div>
          ))}
        {exceptions.data &&
          !exceptions.data.items.some(
            (item) => item.severity === 'critical' || item.severity === 'high',
          ) && <p className="small muted">当前没有严重或高优先级异常。</p>}
        {batches.data?.items.some(
          (batch) => batch.status === 'failed' || batch.error_count > 0,
        ) && (
          <p className="small">
            <Link to="/reconciliation">最近有对账失败或错误批次，查看对账记录 →</Link>
          </p>
        )}
      </section>

      <section className="panel stack">
        <div className="section-head compact">
          <div>
            <span className="eyebrow">自动对账</span>
            <h2>主动渠道核验</h2>
            <p>Worker 每天 10:30 后自动核验上一业务日；财务可在这里手动重跑指定日期或渠道。</p>
          </div>
        </div>
        <div className="filter-bar">
          <Select
            aria-label="手动对账渠道"
            value={runProvider}
            onChange={(e) => setRunProvider(e)}
            disabled={!canWrite}
          >
            <option value="">全部已配置渠道</option>
            <option value="wechat">微信支付</option>
            <option value="alipay">支付宝</option>
            <option value="unionpay">银联 / 银行卡</option>
          </Select>
          <DatePicker
            className="input"
            aria-label="对账业务日期"
            type="date"
            value={businessDate}
            onChange={(e) => setBusinessDate(e)}
            disabled={!canWrite}
          />
          <Button
            loading={running}
            disabled={!canWrite || !businessDate}
            onClick={runReconciliation}
          >
            <RefreshCw size={15} />
            立即对账
          </Button>
          {!canWrite && <span className="small muted">Auditor 仅可查看，不能触发渠道查询。</span>}
        </div>
        <QueryState query={batches} empty={!batches.data?.items.length}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>日期 / 渠道</th>
                  <th>状态</th>
                  <th>核验</th>
                  <th>匹配 / 异常</th>
                  <th>错误</th>
                  <th>完成时间</th>
                </tr>
              </thead>
              <tbody>
                {batches.data?.items.map((batch) => (
                  <tr key={batch.id}>
                    <td>
                      <strong>{batch.business_date}</strong>
                      <span className="sub-cell">{providerLabel(batch.provider)}</span>
                    </td>
                    <td>
                      <Status value={batch.status} label={batchStatusLabel(batch.status)} />
                    </td>
                    <td>{batch.checked_count}</td>
                    <td>
                      {batch.matched_count} / {batch.exception_count}
                    </td>
                    <td>
                      {batch.error_count || 0}
                      {batch.last_error && <span className="sub-cell">{batch.last_error}</span>}
                    </td>
                    <td>{batch.completed_at ? datetime(batch.completed_at) : '运行中'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </section>

      <section className="stack">
        <div className="section-head compact">
          <div>
            <span className="eyebrow">异常队列</span>
            <h2>需要财务关注</h2>
            <p>严重程度优先，其次按最近更新时间排序；处理后会从队列自动消失。</p>
          </div>
        </div>
        <div className="filter-bar">
          <Select
            aria-label="异常类型"
            value={kind}
            onChange={(e) => {
              setKind(e);
              setPage(1);
            }}
          >
            {kinds.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="支付渠道"
            value={provider}
            onChange={(e) => {
              setProvider(e);
              setPage(1);
            }}
          >
            {providers.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <span className="result-count">共 {exceptions.data?.total ?? '…'} 条</span>
        </div>
        <QueryState query={exceptions} empty={!exceptions.data?.items.length}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>严重度</th>
                  <th>类型 / 渠道</th>
                  <th>金额 / 单号</th>
                  <th>状态</th>
                  <th>问题</th>
                  <th>持续时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.data?.items.map((item) => {
                  const reconciliationID =
                    item.kind === 'reconciliation' ? item.id.split(':')[1] : '';
                  const detail = targetPath(item.kind);
                  return (
                    <tr key={item.id}>
                      <td>
                        <span
                          className={`status ${item.severity === 'critical' ? 'status-red' : item.severity === 'high' ? 'status-orange' : ''}`}
                        >
                          {severityLabel(item.severity)}
                        </span>
                      </td>
                      <td>
                        <strong>{kindLabel(item.kind)}</strong>
                        <span className="sub-cell">{providerLabel(item.provider)}</span>
                      </td>
                      <td>
                        <strong>{money(item.amount_minor || 0)}</strong>
                        <span className="sub-cell mono">{item.external_reference || '—'}</span>
                      </td>
                      <td>
                        <Status
                          value={item.status}
                          label={financialStatusLabel(item.kind, item.status)}
                        />
                      </td>
                      <td style={{ minWidth: 220, maxWidth: 380 }}>
                        <strong className="small">{item.code || '需要核实'}</strong>
                        <span className="sub-cell">{item.message}</span>
                      </td>
                      <td>
                        {ageLabel(item.age_seconds)}
                        <span className="sub-cell">{datetime(item.updated_at)}</span>
                      </td>
                      <td>
                        <div className="table-actions">
                          {canWrite &&
                            item.kind === 'payment_intent' &&
                            [
                              'PAYMENT_DUPLICATE_CONFIRMED',
                              'PAYMENT_RECEIPT_REVIEW_REQUIRED',
                            ].includes(item.code) && (
                              <ActionDialog
                                title="记录异常款项处理结果"
                                intent="financial"
                                trigger="记录已处理"
                                submitLabel="确认记录处理结果"
                                path={`/admin/finance/payment-receipts/${item.local_entity_id}/resolve`}
                                description="请先在渠道核对并处理异常款项，填写真实处理凭证。此操作仅关闭异常标记，不会执行退款或修改业务付款。"
                                fields={[
                                  {
                                    name: 'reference',
                                    label: '渠道处理凭证 / 退款单号',
                                    required: true,
                                    min: 4,
                                    max: 255,
                                  },
                                  {
                                    name: 'reason',
                                    label: '处理说明',
                                    type: 'textarea',
                                    required: true,
                                    min: 2,
                                    max: 500,
                                  },
                                ]}
                              />
                            )}
                          {canWrite && item.kind !== 'reconciliation' && (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={refreshing === item.id}
                              onClick={() => refreshException(item)}
                            >
                              查单
                            </Button>
                          )}
                          {canWrite && item.kind === 'reconciliation' && reconciliationID && (
                            <ActionDialog
                              title="处理对账差异"
                              intent="financial"
                              submitLabel="确认处理差异"
                              trigger="标记已处理"
                              path={`/admin/reconciliation/${reconciliationID}/resolve`}
                              fields={[
                                {
                                  name: 'resolution',
                                  label: '处理说明',
                                  type: 'textarea',
                                  min: 2,
                                },
                              ]}
                            />
                          )}
                          {detail && (
                            <Link className="link small" to={detail}>
                              查看记录
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </QueryState>
        <Pagination page={page} total={exceptions.data?.total || 0} size={20} onPage={setPage} />
      </section>
    </>
  );
}
